
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartBar, ChartLine, PieChart as PieChartIcon, Calendar, DollarSign, Users } from 'lucide-react';
import { useAuth, PurchaseRecord } from '@/contexts/MyContext';

// Sample time periods
type TimePeriod = 'day' | 'week' | 'month' | 'year';

// Product colors for consistency
const PRODUCTS = [
  { id: 'launch-guide', name: 'AI App Launch Guide', color: '#9b87f5' },
  { id: 'ai-prompts', name: 'AI Development Swipe File & Prompts', color: '#7E69AB' },
  { id: 'fast-track', name: 'AI App Fast-Track', color: '#6E59A5' },
  { id: 'accelerator', name: 'AI App Accelerator', color: '#D946EF' },
  { id: 'business-mastery', name: 'AI App Business Mastery', color: '#F97316' },
  { id: 'unlimited', name: 'Unlimited AI App Development', color: '#0EA5E9' },
  { id: 'elite-vip', name: 'AI App Elite VIP', color: '#1EAEDB' },
  { id: 'done-for-you', name: 'Done For You Package', color: '#14B8A6' },
  { id: 'advanced', name: 'Advanced Training', color: '#84CC16' },
  { id: 'bonus', name: 'Bonus Content', color: '#EAB308' },
  { id: 'course', name: 'Getting Started', color: '#A855F7' },
  { id: 'all', name: 'All Access', color: '#EC4899' },
];

// Filter purchase records by time period
const filterPurchasesByTimePeriod = (purchases: PurchaseRecord[], period: TimePeriod): PurchaseRecord[] => {
  const now = new Date();
  let cutoffDate = new Date();
  
  switch (period) {
    case 'day':
      cutoffDate.setHours(0, 0, 0, 0);
      break;
    case 'week':
      cutoffDate.setDate(now.getDate() - 7);
      break;
    case 'month':
      cutoffDate.setMonth(now.getMonth() - 1);
      break;
    case 'year':
      cutoffDate.setFullYear(now.getFullYear() - 1);
      break;
  }
  
  // Filter purchases that occurred after the cutoff date
  return purchases.filter(purchase => {
    const purchaseDate = new Date(purchase.date);
    return purchaseDate >= cutoffDate && purchaseDate <= now;
  });
};

// Format date according to the selected time period
const formatDateForPeriod = (dateString: string, period: TimePeriod): string => {
  const date = new Date(dateString);
  
  switch (period) {
    case 'day':
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    case 'week':
      return date.toLocaleDateString([], { weekday: 'short' });
    case 'month':
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    case 'year':
      return date.toLocaleDateString([], { month: 'short' });
    default:
      return date.toLocaleDateString();
  }
};

// Process purchase records for charts
const processDataForCharts = (records: PurchaseRecord[], period: TimePeriod) => {
  // Calculate net amount (purchases - refunds/removals)
  const recordsWithNetAmount = records.map(record => {
    // For admin-add or purchase, amount is positive; for admin-remove or refund, amount is negative
    const netAmount = ['admin-add', 'purchase'].includes(record.transactionType) 
      ? record.amount 
      : -record.amount;
    
    return {
      ...record,
      netAmount,
      formattedDate: formatDateForPeriod(record.date, period)
    };
  });

  // Data for line chart (total sales by date)
  const salesByDate = recordsWithNetAmount.reduce((acc: {[key: string]: number}, cur) => {
    const date = cur.formattedDate;
    acc[date] = (acc[date] || 0) + cur.netAmount;
    return acc;
  }, {});
  
  const lineChartData = Object.keys(salesByDate).map(date => ({
    date,
    amount: salesByDate[date]
  })).sort((a, b) => a.date.localeCompare(b.date));

  // Data for bar chart (sales by product)
  const salesByProduct = recordsWithNetAmount.reduce((acc: {[key: string]: {amount: number, count: number}}, cur) => {
    if (!acc[cur.productName]) {
      acc[cur.productName] = { amount: 0, count: 0 };
    }
    acc[cur.productName].amount += cur.netAmount;
    // Only count as a transaction if it's positive (we don't want to double-count refunds)
    if (cur.netAmount > 0) {
      acc[cur.productName].count += 1;
    } else {
      // For refunds/removals, decrement the count
      acc[cur.productName].count = Math.max(0, acc[cur.productName].count - 1);
    }
    return acc;
  }, {});
  
  const barChartData = Object.keys(salesByProduct)
    .filter(product => salesByProduct[product].amount !== 0) // Filter out products with net zero sales
    .map(product => ({
      name: product,
      amount: salesByProduct[product].amount,
      count: salesByProduct[product].count
    }))
    .sort((a, b) => b.amount - a.amount); // Sort by amount descending

  // Calculate total positive transactions (sales/adds)
  const totalSales = recordsWithNetAmount.reduce((sum, item) => {
    return sum + (item.netAmount > 0 ? item.netAmount : 0);
  }, 0);
  
  // Calculate total negative transactions (refunds/removals)
  const totalRefunds = Math.abs(recordsWithNetAmount.reduce((sum, item) => {
    return sum + (item.netAmount < 0 ? item.netAmount : 0);
  }, 0));

  // Net revenue (sales minus refunds)
  const netRevenue = totalSales - totalRefunds;
  
  // Total count (all transactions, but count refunds/removals as negative)
  const positiveTransactions = recordsWithNetAmount.filter(r => r.netAmount > 0).length;
  const negativeTransactions = recordsWithNetAmount.filter(r => r.netAmount < 0).length;
  const totalCount = positiveTransactions - negativeTransactions;

  return {
    lineChartData,
    barChartData,
    totalSales,
    totalCount: Math.max(0, totalCount), // Ensure count is not negative
    totalRefunds,
    netRevenue,
    detailedRecords: recordsWithNetAmount
  };
};

// Custom tooltip components
const CustomLineChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border border-border/50 p-2 rounded-md shadow-md">
        <p className="font-medium">{label}</p>
        <p className="text-sm">{`Sales: $${Number(payload[0].value).toLocaleString()}`}</p>
      </div>
    );
  }

  return null;
};

const CustomBarChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border border-border/50 p-2 rounded-md shadow-md">
        <p className="font-medium">{payload[0].payload.name}</p>
        <p className="text-sm">{`Revenue: $${Number(payload[0].value).toLocaleString()}`}</p>
        <p className="text-sm">{`Orders: ${payload[0].payload.count}`}</p>
      </div>
    );
  }

  return null;
};

const SalesManager: React.FC = () => {
  const { getPurchaseRecords } = useAuth();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month');
  const [viewType, setViewType] = useState<'overview' | 'detailed'>('overview');
  
  // Get purchase records and filter by time period
  const allPurchaseRecords = getPurchaseRecords();
  const filteredRecords = filterPurchasesByTimePeriod(allPurchaseRecords, timePeriod);
  
  // Process data for charts and statistics
  const { 
    lineChartData, 
    barChartData, 
    totalSales, 
    totalCount, 
    totalRefunds,
    netRevenue,
    detailedRecords 
  } = processDataForCharts(filteredRecords, timePeriod);

  // Get product color
  const getProductColor = (productName: string): string => {
    const product = PRODUCTS.find(p => p.name === productName);
    return product?.color || '#9b87f5'; // Default color if not found
  };

  // Format transaction type for display
  const formatTransactionType = (type: string): string => {
    switch (type) {
      case 'purchase': return 'Purchase';
      case 'refund': return 'Refund';
      case 'admin-add': return 'Admin Added';
      case 'admin-remove': return 'Admin Removed';
      default: return type;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Sales Statistics</h2>
        
        <Tabs value={timePeriod} onValueChange={(val) => setTimePeriod(val as TimePeriod)} className="w-fit">
          <TabsList>
            <TabsTrigger value="day" className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Day</span>
            </TabsTrigger>
            <TabsTrigger value="week" className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Week</span>
            </TabsTrigger>
            <TabsTrigger value="month" className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Month</span>
            </TabsTrigger>
            <TabsTrigger value="year" className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Year</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Sales</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <DollarSign className="h-6 w-6 text-primary mr-2" />
              <span className="text-2xl font-bold">${netRevenue.toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Users className="h-6 w-6 text-primary mr-2" />
              <span className="text-2xl font-bold">{totalCount}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg. Sale Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <ChartBar className="h-6 w-6 text-primary mr-2" />
              <span className="text-2xl font-bold">
                ${totalCount > 0 ? Math.round(netRevenue / totalCount).toLocaleString() : 0}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Sales Over Time</CardTitle>
            <CardDescription>Total sales amount by date</CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <ChartContainer config={{
              sales: { label: 'Sales', theme: { light: '#9b87f5', dark: '#8B5CF6' } },
            }} className="aspect-[4/3]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip content={<CustomLineChartTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="var(--color-sales)" 
                    activeDot={{ r: 8 }} 
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sales by Product</CardTitle>
            <CardDescription>Revenue breakdown by product</CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <ChartContainer config={{
              amount: { label: 'Revenue', theme: { light: '#9b87f5', dark: '#8B5CF6' } },
            }} className="aspect-[4/3]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={false} />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip content={<CustomBarChartTooltip />} />
                  <Bar dataKey="amount" fill="var(--color-amount)">
                    {barChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getProductColor(entry.name)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
            <div className="flex flex-wrap gap-3 mt-4 justify-center">
              {PRODUCTS.map((product) => (
                <div key={product.id} className="flex items-center gap-2">
                  <span 
                    className="w-3 h-3 rounded-full block" 
                    style={{ backgroundColor: product.color }}
                  ></span>
                  <span className="text-xs">{product.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full" onValueChange={(val) => setViewType(val as 'overview' | 'detailed')}>
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-1">
            <ChartBar className="h-4 w-4" />
            <span>Overview</span>
          </TabsTrigger>
          <TabsTrigger value="detailed" className="flex items-center gap-1">
            <ChartLine className="h-4 w-4" />
            <span>Detailed Report</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Sales Summary</CardTitle>
              <CardDescription>Quick overview of sales by product</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {barChartData.length > 0 ? (
                    barChartData.map((item) => (
                      <TableRow key={item.name}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="text-right">{item.count}</TableCell>
                        <TableCell className="text-right">${item.amount.toLocaleString()}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-4 text-muted-foreground">
                        No sales data available for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                {barChartData.length > 0 && (
                  <tfoot className="border-t bg-muted/50 font-medium">
                    <TableRow>
                      <TableCell>Total</TableCell>
                      <TableCell className="text-right">{totalCount}</TableCell>
                      <TableCell className="text-right">${netRevenue.toLocaleString()}</TableCell>
                    </TableRow>
                  </tfoot>
                )}
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="detailed">
          <Card>
            <CardHeader>
              <CardTitle>Detailed Sales Report</CardTitle>
              <CardDescription>Complete breakdown of individual transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailedRecords.length > 0 ? (
                    detailedRecords
                      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Sort by date, newest first
                      .map((record, index) => (
                        <TableRow key={record.id || index}>
                          <TableCell>{new Date(record.date).toLocaleString()}</TableCell>
                          <TableCell>{record.userName}</TableCell>
                          <TableCell>{record.productName}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              record.transactionType === 'admin-remove' || record.transactionType === 'refund'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {formatTransactionType(record.transactionType)}
                            </span>
                          </TableCell>
                          <TableCell className={`text-right ${
                            record.netAmount < 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {record.netAmount < 0 ? '-' : ''}${Math.abs(record.netAmount).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                        No transaction data available for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SalesManager;
